import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";

import Form from "./Form";
import PasswordConfirmationDialog from "components/__reusable/PasswordConfirmDialog";
import ErrorDialog from "components/__reusable/ErrorDialog";
import PromptDialog from "components/__reusable/PromptDialog";
import useAppSelector from "hooks/useAppSelector";
import useAppDispatch from "hooks/useAppDispatch";
import { getStudents, updateStudent } from "features/student/studentAction";
import { setSuccessStatus } from "features/success/successSlice";
import { setErrorStatus } from "features/error/errorSlice";
import {
  getDeletionToken,
  deleteStudentWithToken,
  checkStudentHasRegisteredParent,
} from "services/studentServices";
import { StudentSortValue } from "config/constants";
import type { StudentFormData } from "types/Student";
import LoadingDialog from "components/__reusable/LoadingDialog";
import type { UpdatedTrainingPicture } from "types/TrainingPicture";

const UpdateForm = () => {
  const { t } = useTranslation();
  const { classId, id } = useParams();
  const { students, loading } = useAppSelector((state) => state.student);
  const dispatch = useAppDispatch();
  const fetchStudents = () =>
    students.length === 0 &&
    dispatch(
      getStudents({
        classId: !isNaN(parseInt(classId || "")) ? parseInt(classId || "") : 0,
        sortValue: StudentSortValue.name_asc,
      }),
    );
  const callFetchStudents = useCallback(fetchStudents, [
    classId,
    dispatch,
    getStudents,
  ]);
  useEffect(() => {
    callFetchStudents();
  }, [callFetchStudents]);

  const [student, setStudent] = useState<StudentFormData>({
    first_name: "",
    first_name_kata: "",
    last_name_kata: "",
    last_name: "",
    birthday: "",
    graduation_date: "",
    class_id: "",
    is_ng: false,
  });
  useEffect(() => {
    if (students.length === 0) return;
    if (isNaN(parseInt(id || ""))) return;
    const student = students.find(
      (student) => student.id === parseInt(id || ""),
    );
    if (!student) return;
    setStudent({
      first_name: student.first_name,
      first_name_kata: student.first_name_kata,
      last_name_kata: student.last_name_kata,
      last_name: student.last_name,
      birthday: student.birthday,
      graduation_date: student.graduation_date,
      class_id: student.class_id.toString(),
      is_ng: student.is_ng,
    });
  }, [students, id]);

  const update = async (
    formData: StudentFormData,
    pictures: File[],
    updatedTrainingPictures: UpdatedTrainingPicture[] | undefined,
  ) => {
    const updateAction = await dispatch(
      updateStudent({
        id: parseInt(id || ""),
        formData,
        trainingPictures: pictures,
        updatedTrainingPictures: updatedTrainingPictures || [],
      }),
    );
    if (isFulfilled(updateAction)) {
      dispatch(
        setSuccessStatus({
          title: "_student_form._update_student_complete",
          message: "_student_form._update_student_complete_content",
        }),
      );
    } else {
      dispatch(setErrorStatus(""));
    }
  };

  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [showFinalConfirmation, setShowFinalConfirmation] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [validationLoading, setValidationLoading] = useState(false);

  const handleDeleteButtonClick = () => {
    setShowPasswordPrompt(true);
  };

  const handlePasswordConfirm = async (password: string) => {
    setTokenLoading(true);
    try {
      const token = await getDeletionToken(password, "delete_student");
      localStorage.setItem("deletion_token", token);
      setShowPasswordPrompt(false);

      setValidationLoading(true);
      const hasParent = await checkStudentHasRegisteredParent(
        parseInt(id || ""),
      );
      setValidationLoading(false);

      if (hasParent) {
        setShowErrorDialog(true);
        localStorage.removeItem("deletion_token");
        return;
      }

      setShowFinalConfirmation(true);
    } catch (error: any) {
      dispatch(
        setErrorStatus(
          error.response?.data?.message || "パスワードが正しくありません",
        ),
      );
    } finally {
      setTokenLoading(false);
    }
  };

  const handleCancel = () => {
    setShowPasswordPrompt(false);
    setShowFinalConfirmation(false);
    setShowErrorDialog(false);
    localStorage.removeItem("deletion_token");
  };

  const handleErrorDialogClose = () => {
    setShowErrorDialog(false);
  };

  const remove = async () => {
    const token = localStorage.getItem("deletion_token");
    if (!token) {
      dispatch(setErrorStatus("認証トークンが見つかりません"));
      setShowFinalConfirmation(false);
      return;
    }

    setDeleteLoading(true);
    try {
      await deleteStudentWithToken(parseInt(id || ""), token);
      localStorage.removeItem("deletion_token");
      setShowFinalConfirmation(false);
      dispatch(
        setSuccessStatus({
          title: "_student_form._delete_student_success",
          message: "_student_form._delete_student_success_content",
          onClose: () => {
            window.history.back();
          },
        }),
      );
    } catch (error: any) {
      setShowFinalConfirmation(false);
      dispatch(
        setErrorStatus(error.response?.data?.message || "エラーが発生しました"),
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-1">
      <Form
        isUpdate={true}
        student={student}
        onSubmit={update}
        actionLeftContent={
          <button
            type="button"
            className="font-bold text-[#5B5B5B] transition-colors duration-200 ease-out hover:text-[#ff0000]"
            onClick={handleDeleteButtonClick}
          >
            {t("_student_form._delete_student_button")}
          </button>
        }
      />
      {loading && <LoadingDialog />}
      {showErrorDialog && (
        <ErrorDialog
          title={t("_generic._error", { defaultValue: "エラー" })}
          message={t("_student._cannot_delete_registered_parent_message", {
            defaultValue:
              "園児と紐づけされた保護者アカウントがあります。\n該当の保護者アカウントを削除してから操作してください。",
          })}
          onClose={handleErrorDialogClose}
        />
      )}
      {showPasswordPrompt && (
        <PasswordConfirmationDialog
          onCancel={handleCancel}
          onConfirm={handlePasswordConfirm}
          isLoading={tokenLoading || validationLoading}
          title={t("_student._delete_student", {
            defaultValue: "園児を削除する",
          })}
          message={
            <div className="space-y-3">
              <p className="whitespace-pre-wrap">
                {t("_student._deletion_warning_1", {
                  defaultValue:
                    "園児を削除すると、該当園児の登録情報や写真データがすべて削除され、復元することができません。\n園児と紐づいている保護者アカウントが存在する場合、削除できません。",
                })}
              </p>
              <p className="pt-2">
                {t("_student._deletion_proceed_instructions", {
                  defaultValue:
                    "削除処理に進む場合は施設のパスワードを入力し、「同意して進む」を選択してください。",
                })}
              </p>
            </div>
          }
        />
      )}
      {showFinalConfirmation && (
        <PromptDialog
          title={t("_class.confirm_deletion", {
            defaultValue: "本当に削除しますか？",
          })}
          message={
            <div className="space-y-3 text-center">
              <p className="text-lg font-medium whitespace-pre-wrap">
                {t("_student._delete_warning", {
                  name: `${student.last_name} ${student.first_name}`,
                })}
              </p>
              <p className="text-sm text-red-500">
                {t("_class.deleted_data_unrecoverable", {
                  defaultValue: "削除した園児のデータは復元することができません。",
                })}
              </p>
            </div>
          }
          onCancel={handleCancel}
          onConfirm={remove}
          cancelText={t("_generic._cancel")}
          confirmText={t("_generic._delete")}
          isLoading={deleteLoading}
        />
      )}
    </div>
  );
};

export default UpdateForm;
